import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {theme as antdTheme} from 'antd'
import {DARK_MODE, LIGHT_MODE, LIGHTEST_GRAY_COLOR, RUST_BLUE_COLOR} from '../constants'
import {isAuth} from '../utils/auth'

const THEME_STORAGE_KEY = 'tracer-theme'

// The page background behind the app: antd's Layout bodyBg for each theme
// (ComponentThemeGenerator.js). public/index.html paints the same colours
// before the bundle loads, so keep the two in step.
const LAYOUT_BACKGROUND = {[DARK_MODE]: RUST_BLUE_COLOR, [LIGHT_MODE]: LIGHTEST_GRAY_COLOR}

const getInitialMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === DARK_MODE || stored === LIGHT_MODE) return stored
  } catch {
    // localStorage may be unavailable (SSR, private browsing edge cases)
  }
  return DARK_MODE
}

const ThemeContext = createContext()

export const ThemeProvider = ({ children }) => {
  const [savedMode, setMode] = useState(getInitialMode)
  // Signed-out pages (sign in, the OAuth callback, not permitted, logged out)
  // are always dark, whatever the user picked inside the app - the theme toggle
  // only exists once signed in, so a light choice could not be undone there.
  // The saved choice is untouched and applies again after sign in. Seeded from
  // the token so the first paint is already right; Layout keeps it in step.
  const [forceDark, setForceDark] = useState(() => !isAuth())
  const mode = forceDark ? DARK_MODE : savedMode
  const { darkAlgorithm, defaultAlgorithm } = antdTheme
  const algorithm = mode === DARK_MODE ? darkAlgorithm : defaultAlgorithm
  const transitionTimerRef = useRef(null)

  // Whatever shows around or behind the layout - the fade while the theme
  // switches, or a moment before a page paints - matches the theme in use.
  useEffect(() => {
    document.documentElement.style.backgroundColor = LAYOUT_BACKGROUND[mode] || RUST_BLUE_COLOR
  }, [mode])

  /**
   * Switch theme with an opacity mask to prevent visible flicker.
   *
   * Antd's CSS-in-JS engine swaps entire <style> tags when the algorithm
   * changes, which can cause a brief frame where styles are missing.
   * By fading #root to opacity 0 first, applying the theme, then fading
   * back in, the browser never shows the inconsistent intermediate state.
   *
   * Rapid clicks are debounced — only the final theme choice takes effect.
   */
  const persistMode = useCallback((newMode) => {
    // Debounce rapid toggles — cancel any pending transition
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current)
    }

    const root = document.getElementById('root')
    if (!root) {
      setMode(newMode)
      try { localStorage.setItem(THEME_STORAGE_KEY, newMode) } catch {}
      return
    }

    // Phase 1: Fade out
    root.style.opacity = '0'

    // Phase 2: After fade-out completes, swap the theme
    transitionTimerRef.current = setTimeout(() => {
      transitionTimerRef.current = null
      setMode(newMode)
      try { localStorage.setItem(THEME_STORAGE_KEY, newMode) } catch {}

      // Phase 3: After React commits the new styles, fade back in
      requestAnimationFrame(() => {
        root.style.opacity = '1'
      })
    }, 100)
  }, [])

  return (
    <ThemeContext.Provider value={{ mode, setMode: persistMode, algorithm, setForceDark }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useThemeContext = () => useContext(ThemeContext)
